import type { JourneyStep, JourneyStepId } from '@/lib/journey';

/**
 * Die vier Werkbank-/Kapitel-Ziele der Leiter, an einer Stelle.
 *
 * Entscheidung E3 des Plans (2026-09-16, Spec Abschnitt 5.3): `useJourney.targets`,
 * die `SuccessScoreboard`-Navigation und das Kapitel "Bei Google" lesen alle von
 * hier. Ohne eine gemeinsame Quelle drohten drei Kopien derselben Query-String-
 * Zeichenkette auseinanderzulaufen, sobald sich ein Filterparameter aendert.
 */

/**
 * Reiter "Wartend" plus der Filter `awaiting_upload` (Abschnitt 5.2): nur
 * Produkte, die noch NICHT bei Google liegen. Ziel von Schritt 2 (optimize).
 */
export const WORKBENCH_TO_REVIEW_TARGET = '/workbench?status=pending&scope=awaiting_upload';

/**
 * Reiter "Freigegeben" plus derselbe Filter. Ziel von Schritt 3 (approve).
 */
export const WORKBENCH_READY_TARGET = '/workbench?status=approved&scope=awaiting_upload';

/**
 * Anker auf das Kapitel "Bei Google" im Feed-Check (Abschnitt 6). Ziel von
 * Schritt 4 (upload).
 */
export const LIVE_CHAPTER_TARGET = '/#live';

/**
 * Anker auf das Ads-Kapitel im Feed-Check. Ziel der Schritte 5 (ads_connect)
 * und 6 (ads_campaigns, Teaser).
 */
export const ADS_CHAPTER_TARGET = '/#ads';

/**
 * Das Ziel einer HANDLUNG, nicht eines Chips (UX-Audit WP5, A2O-01).
 *
 * Die Handlungskarte bietet bei Schritt `approve` "N Produkte freigeben" an,
 * mit N = wartend UND noch nicht bei Google. Freigegeben wird im Reiter
 * "Offen". Das Leiterziel `targets.approve` zeigt dagegen auf den Reiter
 * "Freigegeben", weil der Chip von Schritt 3 den freigegebenen Bestand zaehlt.
 * Beide Ziele sind richtig, aber fuer verschiedene Fragen; deshalb zwei
 * Funktionen und keine gemeinsame Map.
 */
export function actionTargetFor(step: JourneyStepId, targets: Record<JourneyStepId, string>): string {
  if (step === 'approve') return WORKBENCH_TO_REVIEW_TARGET;
  return targets[step];
}

/**
 * Reiter "Freigegeben" OHNE den Filter "noch nicht bei Google": alles, was
 * schon freigegeben ist. Ziel erledigter Schritte (A3-09).
 */
export const WORKBENCH_APPROVED_TARGET = '/workbench?status=approved';

/**
 * Das Ziel des Chips von Schritt 3 ("Freigabe"). Traegt der Chip eine Zahl
 * (freigegeben, noch nicht bei Google), oeffnet er genau diese Liste. Steht
 * der Zeiger auf Schritt 3, weil Produkte auf die Freigabe WARTEN, der Chip
 * aber keine Zahl hat, fuehrt er dorthin, wo freigegeben wird.
 */
export function approveChipTarget(ready: number, toApprove: number): string {
  if (ready > 0) return WORKBENCH_READY_TARGET;
  if (toApprove > 0) return WORKBENCH_TO_REVIEW_TARGET;
  // UX-Audit A3-09 (Dennis-Entscheidung 13.10.2026, Option 3A): "nichts
  // offen" zaehlt null, die gefilterte Liste waere leer und saehe nach einem
  // Fehler aus ("Keine passenden Produkte"). Ein erledigter Schritt oeffnet
  // deshalb den ganzen freigegebenen Bestand. "Zahl = Liste" bleibt gewahrt,
  // weil der erledigte Chip keine Zahl traegt.
  return WORKBENCH_APPROVED_TARGET;
}

/**
 * Was ein Klick auf einen Chip der Fortschrittsleiste tut (UX-Audit A3-09,
 * Dennis-Entscheidung 13.10.2026, Option 3A). Bewusst NUR fuer den Klick auf
 * den Chip und nicht in `targets`: `targets.optimize` ist zugleich das Ziel
 * der Handlung "optimieren" (`actionTargetFor`, `NextStepCta`), und die soll
 * sich nicht aendern.
 *
 * - Feed-Schritt auf der Startseite: das Ziel `/` ist die Seite, auf der man
 *   schon steht; der Klick oeffnet stattdessen den Feed-Switcher.
 * - "Optimiert · geprueft" (nichts wartet, es gibt aber optimierte Produkte):
 *   die gefilterte Liste der wartenden Produkte waere leer, der Klick oeffnet
 *   den freigegebenen Bestand. Bei erschoepftem Kontingent bleibt `/pricing`.
 * - alles andere: das Leiterziel aus `targets`.
 */
export type ChipClick = { kind: 'navigate'; to: string } | { kind: 'open-switcher' };

export function chipClickFor(
  step: JourneyStep,
  targets: Record<JourneyStepId, string>,
  pathname: string,
): ChipClick {
  const target = targets[step.id];
  if (step.id === 'connect' && target === '/' && pathname === '/') return { kind: 'open-switcher' };
  if (
    step.detail.kind === 'optimize' &&
    step.count === 0 &&
    step.detail.optimized > 0 &&
    target === WORKBENCH_TO_REVIEW_TARGET
  ) {
    return { kind: 'navigate', to: WORKBENCH_APPROVED_TARGET };
  }
  return { kind: 'navigate', to: target };
}
